import manifest from "../../../../config/questionnaire-vocabularies.json";

/**
 * Shared runtime vocabularies (S3.19), owned by
 * `config/questionnaire-vocabularies.json`. Python reads the same file
 * (`apps/python-api/models/questionnaire/vocabularies.py`; survey-engine's
 * `digital_twins/prompts/question_utils.py` for the N/A value). A closed
 * vocabulary is the keys of its map, in order, so the TypeScript union and the
 * Python `Literal` (whose member order the provider schemas keep) both come
 * from it.
 */
if (manifest.schemaVersion !== "questionnaire-vocabularies.v1") {
  throw new TypeError("Unsupported questionnaire vocabularies registry.");
}

/**
 * The stored value of a rating's declared N/A answer. The compiler writes it
 * as the choice value; analysis and the twins read it back.
 */
export const NOT_APPLICABLE_VALUE: string = manifest.notApplicableValue;

/** A continuous response's unit (`ContinuousResponse.unit`). */
export type ContinuousUnitId = keyof typeof manifest.continuousUnits;
export const CONTINUOUS_UNITS = Object.keys(
  manifest.continuousUnits,
) as ReadonlyArray<ContinuousUnitId>;

/** Where an ad runs (`AdAsset.placement`). */
export type AdPlacement = keyof typeof manifest.adPlacements;
export const AD_PLACEMENTS = Object.keys(
  manifest.adPlacements,
) as ReadonlyArray<AdPlacement>;

/**
 * How often a subscription price is charged (`ProductAssetPrice.billingPeriod`).
 * Absent means no period: a one-time price, or one charged on another basis,
 * which its product's description states.
 */
export type BillingPeriod = keyof typeof manifest.billingPeriods;
export const BILLING_PERIODS = Object.keys(
  manifest.billingPeriods,
) as ReadonlyArray<BillingPeriod>;

/**
 * Every unit a stored duration answer may carry (`ContinuousResponse.durationUnit`).
 * A stored draft keeps any of them; a new or edited question uses only the
 * exposed ones (the validator's `unsupported_duration_unit`, and every model
 * writer's format).
 */
export type DurationUnit = keyof typeof manifest.durationUnits;
export const DURATION_UNITS = Object.keys(
  manifest.durationUnits,
) as ReadonlyArray<DurationUnit>;
/** The duration units a question may use, in registry order. */
export const DURATION_EXPOSED_UNITS: ReadonlyArray<DurationUnit> =
  DURATION_UNITS.filter((unit) => manifest.durationUnits[unit].exposed);
if (DURATION_EXPOSED_UNITS.length === 0) {
  throw new TypeError("The questionnaire vocabularies expose no duration unit.");
}

/** What a tested message claims (`MessageAsset.claimType`). */
export type MessageClaimType = keyof typeof manifest.messageClaimTypes;
export const MESSAGE_CLAIM_TYPES = Object.keys(
  manifest.messageClaimTypes,
) as ReadonlyArray<MessageClaimType>;

/**
 * Where a study's brand came from (`StudySpecStudyBrand.source`): the
 * objective and intake, the organization (PLAN suggested it because it fits
 * the objective) or the user's edit of the "Study brand" field.
 */
export type StudyBrandSource = keyof typeof manifest.studyBrandSources;
export const STUDY_BRAND_SOURCES = Object.keys(
  manifest.studyBrandSources,
) as ReadonlyArray<StudyBrandSource>;

/**
 * Where a tested asset's name came from, as every model writer of one states
 * it (`nameSource`): the name the user gave it (`label`), the user's own words
 * of its copy (`content`), both exact, or a name the model wrote from the
 * user's words about the asset (`adapted`). The name's provenance is stamped
 * from it (`nameSourceProvenance`).
 */
export type AssetNameSource = keyof typeof manifest.assetNameSources;
export const ASSET_NAME_SOURCES = Object.keys(
  manifest.assetNameSources,
) as ReadonlyArray<AssetNameSource>;

/**
 * Where a tested asset's description came from, as every model writer of one
 * states it (`descriptionSource`): pieces of the user's own words (`user`),
 * exact, or a description the model wrote from them by the wording rule
 * (`adapted`). Its provenance is stamped from it
 * (`descriptionSourceProvenance`).
 */
export type AssetDescriptionSource = keyof typeof manifest.assetDescriptionSources;
export const ASSET_DESCRIPTION_SOURCES = Object.keys(
  manifest.assetDescriptionSources,
) as ReadonlyArray<AssetDescriptionSource>;
